import { gql } from '@apollo/client';
import { HStack } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PowerBadge from '~components/displays/PowerBadge';
import PresenceBadge from '~components/displays/PresenceBadge';
import { TruncatedCell } from '~components/organization/AccessRequests/AccessRequestsSection';
import { HandleApiError } from '~graphql/HandleApiError';
import {
  ServiceAdminSearchDevicesQuery,
  useServiceAdminSearchDevicesQuery,
} from './__generated__/DeviceSearchResults.graphql';
import { AccessStateCell } from './AccessStateCell';
import { RequestAccessModal } from './RequestAccessModal';
import { SearchResultColumn, SearchResultsTable, useSearchPagination } from './SearchResultsTable';

type DeviceResult = ServiceAdminSearchDevicesQuery['serviceAdminSearchDevices']['results'][number];

export function DeviceSearchResults({ searchTerm }: { searchTerm: string }) {
  const { t } = useTranslation();
  const pagination = useSearchPagination();
  const [requestingAccessTo, setRequestingAccessTo] = useState<{ id: string; name: string } | null>(
    null,
  );

  const { data, loading, error, refetch } = useServiceAdminSearchDevicesQuery({
    variables: { searchTerm, pageSize: pagination.pageSize, pageNumber: pagination.pageIndex },
    fetchPolicy: 'cache-and-network',
  });

  const columns = useMemo<Array<SearchResultColumn<DeviceResult>>>(
    () => [
      {
        id: 'status',
        header: t('status'),
        render: (device) => (
          <HStack>
            <PresenceBadge
              variant="small"
              display={{
                __typename: 'Display',
                id: device.id,
                presence: { __typename: 'DisplayPresence', connected: device.connected },
              }}
            />
            {device.power && (
              <PowerBadge
                variant="small"
                display={{
                  __typename: 'Display',
                  id: device.id,
                  power: { __typename: 'PowerState', reported: device.power, desired: null },
                }}
              />
            )}
          </HStack>
        ),
      },
      {
        id: 'alias',
        header: t('alias'),
        render: (device) => (
          <TruncatedCell text={device.alias ?? '-'} maxWidth="40" fontWeight="semibold" />
        ),
      },
      { id: 'mac', header: t('macAddress'), render: (device) => device.mac },
      {
        id: 'serialNumber',
        header: t('serialNumber'),
        render: (device) => <TruncatedCell text={device.serialNumber} maxWidth="40" />,
      },
      {
        id: 'customer',
        header: t('customer'),
        render: (device) => <TruncatedCell text={device.customerName} maxWidth="40" />,
      },
      {
        id: 'organization',
        header: t('organisation'),
        render: (device) => <TruncatedCell text={device.organizationName} maxWidth="40" />,
      },
      {
        id: 'accessState',
        header: '',
        render: (device) => (
          <AccessStateCell
            organizationId={device.organizationId}
            accessState={device.accessState}
            onRequestAccess={() =>
              setRequestingAccessTo({ id: device.organizationId, name: device.organizationName })
            }
          />
        ),
      },
    ],
    [t],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <>
      <SearchResultsTable
        isLoading={loading && !data}
        rows={data?.serviceAdminSearchDevices.results ?? []}
        totalCount={data?.serviceAdminSearchDevices.totalCount ?? 0}
        columns={columns}
        pagination={pagination}
      />
      <RequestAccessModal
        organization={requestingAccessTo}
        onClose={() => setRequestingAccessTo(null)}
        onRequested={refetch}
      />
    </>
  );
}

DeviceSearchResults.graphql = {
  queries: {
    ServiceAdminSearchDevices: gql`
      query ServiceAdminSearchDevices($searchTerm: String!, $pageSize: Int, $pageNumber: Int) {
        serviceAdminSearchDevices(
          searchTerm: $searchTerm
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          results {
            id
            alias
            mac
            serialNumber
            connected
            power
            customerName
            organizationId
            organizationName
            accessState
          }
        }
      }
    `,
  },
};
